import { useState } from 'react'
import { AGENT_BY_ID } from '../../config/competition'
import type { ChainId } from '../../core/types'

/* Imagery lives in public/assets and is fetched once by `npm run assets`, so
   the running board makes no third-party requests for pictures and works
   offline. Anything missing degrades to a monogram tile rather than a broken
   image icon. */

function Monogram({ text, size, title }: { text: string; size: number; title?: string }) {
  return (
    <span
      className="mono-tile"
      style={{ width: size, height: size, fontSize: Math.max(7, size * 0.42) }}
      title={title}
      aria-hidden="true"
    >
      {text.slice(0, 2)}
    </span>
  )
}

export function TokenIcon({ symbol, size = 18 }: { symbol: string; size?: number }) {
  const [failed, setFailed] = useState(false)
  if (failed) return <Monogram text={symbol} size={size} title={symbol} />
  return (
    <img
      className="tok"
      src={`/assets/tokens/${symbol}.png`}
      width={size}
      height={size}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
    />
  )
}

export function ChainIcon({ chain, size = 13 }: { chain: ChainId; size?: number }) {
  const [failed, setFailed] = useState(false)
  if (failed) return null
  return (
    <img
      className="chn"
      src={`/assets/chains/${chain}.png`}
      width={size}
      height={size}
      alt={chain}
      title={chain}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  )
}

/** Token mark with the chain it trades on tucked into the corner. */
export function AssetIcon({
  symbol,
  chain,
  size = 20,
}: {
  symbol: string
  chain: ChainId
  size?: number
}) {
  return (
    <span className="asset" style={{ width: size, height: size }}>
      <TokenIcon symbol={symbol} size={size} />
      <span className="asset__chain">
        <ChainIcon chain={chain} size={Math.round(size * 0.46)} />
      </span>
    </span>
  )
}

/** Deterministic identicon, recoloured to the agent's own series hue. */
export function AgentAvatar({ agentId, size = 22 }: { agentId: string; size?: number }) {
  const [failed, setFailed] = useState(false)
  const a = AGENT_BY_ID[agentId]
  if (!a) return null
  if (failed) return <Monogram text={a.name} size={size} title={a.name} />
  return (
    <img
      className="avatar"
      src={`/assets/agents/${a.id}.svg`}
      width={size}
      height={size}
      alt=""
      title={a.name}
      style={{ borderColor: a.color }}
      onError={() => setFailed(true)}
    />
  )
}
